import moment from "moment";
import { Cell, CellValue } from "exceljs";
import Msg from "./message";
import ExcelImportUtils from "./excel-import-utils";
import {
    enumEach, enumValues, getEntries, longest, normalize, occurs, regexpEscape, replaceAll, uniq
} from "../utils";
import IConfigNumber from "../interface/field-cfg-number";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IApiField from "../interface/api/api-field";
import Global from "../global";
import Message from "../enum/message";
import LogicalValue from "../enum/logical-value";
import FieldType from "../enum/field-type";
import DecimalSeparator from "../enum/decimal-separator";
import DateType from "../enum/date-type";
import DatePeriod from "../enum/date-period";
import CurrencyPosition from "../enum/currency-position";
import DefaultConfig from "../default-config";
import FormatsDateTime from "../data/formats-date-time";

class ExcelParserSingleton {

    private readonly PhoneMinDigits: number = 10;

    private readonly PhoneMaxDigits: number = 13

    private readonly PhoneRegex: RegExp = /\+?\d{10,13}/

    private readonly UrlRegex: RegExp = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/

    private readonly EmailRegex: RegExp = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    private readonly CheckTrue: string[] = [LogicalValue.True, ...Msg.getAll(Message.checktrue)].map((v: string) => normalize(v.toLowerCase().trim()))

    private readonly CheckFalse: string[] = [LogicalValue.False, ...Msg.getAll(Message.checkfalse)].map((v: string) => normalize(v.toLowerCase().trim()));

    private readonly CheckIndetermine: string[] = Msg.getAll(Message.checkindetermine).map((v: string) => normalize(v.toLowerCase().trim()));

    private readonly CheckValues: string[] = [...this.CheckTrue, ...this.CheckFalse, ...this.CheckIndetermine];

    private readonly TextareaLength: number = 50;

    public readonly EmailLinkPrefix: string = 'mailto:';

    public isCheckValueTrue(val: string): boolean {
        return this.CheckTrue.includes(normalize(val.toLowerCase().trim()));
    }

    public isCheckValueFalse(val: string): boolean {
        return this.CheckFalse.includes(normalize(val.toLowerCase().trim()));
    }

    public parseField(cells: Cell[]): Partial<IApiField> {
        const values: CellValue[] = cells.map((cell: Cell) => ExcelImportUtils.getCellValue(cell.value));
        if (ExcelImportUtils.hasAnyValue(values)) {
            /* Check if this is a simple type */
            if (this.isSimpleType(values, this.isEmail.bind(this))) {
                return { uiType: FieldType.email };
            } else if (this.isSimpleType(values, this.isUrl.bind(this))) {
                return { uiType: FieldType.url };
            } else if (this.isSimpleType(values, this.isCheck.bind(this))) {
                return { uiType: FieldType.check };
            } else if (this.isSimpleType(values, this.isPhone.bind(this))) {
                return { uiType: FieldType.phone, uiConfig: DefaultConfig[FieldType.phone] };
            } else if (this.isSimpleType(values, this.isTextarea.bind(this))) {
                return { uiType: FieldType.textarea };
            }
            /* Check if this is a raw datetime values */
            if (this.isDatetimeRaw(values)) {
                return {
                    uiConfig: this.getDatetimeConfigRaw(cells),
                    uiType: FieldType.datetime
                };
            }
            /* Check if this is a string datetime values */
            const dConfig: IConfigDatetime | null = this.isDatetimeString(values);
            if (dConfig) {
                return {
                    uiConfig: dConfig,
                    uiType: FieldType.datetime
                };
            }
            /* Check if this is a raw number values */
            if (this.isNumberRaw(values)) {
                return {
                    uiConfig: this.getNumberConfigRaw(cells),
                    uiType: FieldType.number
                };
            }
            /* Check if this is a string number values */
            const nConfig: IConfigNumber | null = this.isNumberString(values);
            if (nConfig) {
                return {
                    uiConfig: nConfig,
                    uiType: FieldType.number
                };
            }
        }
        return { uiType: FieldType.text };
    }

    private isSimpleType(values: CellValue[], fn: Function): boolean {
        for (let i = 0; i < values.length; i++) {
            if (ExcelImportUtils.hasValue(values[i]) && (typeof values[i] !== 'string' || !fn(values[i]))) {
                return false;
            }
        }
        return true;
    }

    private isEmail(value: string): boolean {
        if (value.indexOf(this.EmailLinkPrefix) === 0) {
            return true;
        }
        return this.EmailRegex.test(value);
    }

    private isUrl(value: string): boolean {
        return this.UrlRegex.test(value);
    }

    private isCheck(value: string): boolean {
        return !value.length || this.CheckValues.includes(normalize(value.toLowerCase().trim()));
    }

    private isTextarea(value: string): boolean {
        return value.length > this.TextareaLength;
    }

    private isPhone(value: string): boolean {
        if (this.PhoneRegex.test(value)) {
            const digitsOnly: string = value.toString().replace(/\D/g, '');
            if (digitsOnly.length >= this.PhoneMinDigits && digitsOnly.length <= this.PhoneMaxDigits) {
                return true;
            }
        }
        return false;
    }

    private isDatetimeRaw(values: CellValue[]): boolean {
        for (let i = 0; i < values.length; i++) {
            if (ExcelImportUtils.hasValue(values[i]) && !(values[i] instanceof Date)) {
                return false;
            }
        }
        return true;
    }

    private isDatetimeString(values: CellValue[]): IConfigDatetime | null {
        for (let i = 0; i < FormatsDateTime.length; i++) {
            const format: string = FormatsDateTime[i];
            let valid: boolean = true;
            for (let j = 0; j < values.length; j++) {
                const value: CellValue = values[j];
                if (typeof value === 'string') {
                    if (ExcelImportUtils.hasValue(value) && !(moment(value, format, true).isValid())) {
                        valid = false;
                        break;
                    }
                } else {
                    valid = false;
                }
            }
            if (valid) {
                return Object.assign({}, DefaultConfig[FieldType.datetime](), {
                    type: format.includes('H') ? DateType.DateTime : DateType.DateOnly,
                    format
                }) as IConfigDatetime;
            }
        }
        return null;
    }

    private isNumberRaw(values: CellValue[]): boolean {
        for (let i = 0; i < values.length; i++) {
            if (ExcelImportUtils.hasValue(values[i]) && typeof values[i] !== 'number') {
                return false;
            }
        }
        return true;
    }

    private isNumberString(values: CellValue[]): IConfigNumber | null {
        let decimalSeparator: DecimalSeparator | null = null;
        const seps: DecimalSeparator[][] = [];
        for (let j = 0; j < values.length; j++) {
            const value: CellValue = values[j];
            if (ExcelImportUtils.hasValue(value)) {
                if (typeof value !== 'string') {
                    return null;
                } else {
                    const val: string = replaceAll(value.trim(), ' ', '');
                    if (isNaN(Number(replaceAll(replaceAll(val, '.', ''), ',', '')))) {
                        return null;
                    } else {
                        const thousandSeparators: number[] = [];
                        enumEach(DecimalSeparator, (sep: DecimalSeparator) => {
                            const occurrences: number = occurs(val, sep).length;
                            if (occurrences > 1) {
                                thousandSeparators.push(occurrences);
                            }
                        });
                        if (thousandSeparators.length > 1) {
                            return null;
                        } else {
                            seps.push(this.getDecimalSeparators(val));
                        }
                    }
                }
            }
        }
        enumEach(DecimalSeparator, (sep: DecimalSeparator) => {
            let valid: boolean = true;
            seps.forEach((s: DecimalSeparator[]) => {
                if (!s.includes(sep)) {
                    valid = false;
                }
            });
            if (valid && !decimalSeparator) {
                decimalSeparator = sep;
            }
        });
        if (decimalSeparator) {
            let decimalLength: number = 0;
            values.forEach((value: CellValue) => {
                if (ExcelImportUtils.hasValue(value)) {
                    const val: string = replaceAll(value as string, ' ', '');
                    const split: string[] = val.split(decimalSeparator as string);
                    if (split.length > 1) {
                        if (split[1].length > decimalLength) {
                            decimalLength = split[1].length;
                        }
                    }

                }
            });
            return Object.assign({}, DefaultConfig[FieldType.number](), {
                decimalSeparator,
                decimalLength
            }) as IConfigNumber;
        } else {
            return null;
        }
    }

    private getDecimalSeparators(val: string): DecimalSeparator[] {
        const result: DecimalSeparator[] = [];
        enumEach(DecimalSeparator, (sep: DecimalSeparator) => {
            const matches: number = occurs(val, sep).length;
            if (!matches) {
                result.push(sep);
            } else if (matches === 1) {
                const seps: string = enumValues(DecimalSeparator).join('');
                const all: string[] = val.match(new RegExp(`[${regexpEscape(seps)}]`, 'g')) || [];
                if (all.slice(-1)[0] === sep) {
                    result.push(sep);
                }
            }
        });
        return result;
    }

    /* Private Methods - UI Config */

    private getDatetimeConfigRaw(cells: Cell[]): IConfigDatetime {
        const formats: string[] = uniq(this.getCellFormats(cells).map((origin: string) => {
            return replaceAll(`${origin}`.toUpperCase(), ':MM', ':mm');
        }));
        const compatible: string[] = formats.filter((format: string) => {
            return FormatsDateTime.includes(format);
        });
        if (compatible.length) {
            return this.parseDatetimeFormat(longest(formats));
        } else {
            const format: string = longest(formats);
            if (format.includes('h') || format.includes('H')) {
                return DefaultConfig[FieldType.datetime](DateType.DateTime);
            } else {
                return DefaultConfig[FieldType.datetime](DateType.DateOnly);
            }
        }
    }

    private getNumberConfigRaw(cells: Cell[]): IConfigNumber {
        const formats: string[] = this.getCellFormats(cells);
        if (formats.length) {
            return this.parseNumberFormat(longest(formats));
        }
        return DefaultConfig[FieldType.number]();
    }

    private getCellFormats(cells: Cell[]): string[] {
        const formats: string[] = [];
        cells.forEach((c: Cell) => {
            if (c.numFmt) {
                formats.push(c.numFmt);
            }
        });
        return uniq(formats);
    }

    private parseNumberFormat(format: string): IConfigNumber {
        const CurrencyWrappers: string[][] = [['[$', ']'], ['"', '"']];
        let currencySymbol: string = '';
        let currencyPosition: CurrencyPosition = CurrencyPosition.Right;
        CurrencyWrappers.forEach((w: string[]) => {
            const r: RegExp = new RegExp(`${regexpEscape(w[0])}(.*?)${regexpEscape(w[1])}`, 'g');
            const entry: string | null = getEntries(format, r)[0] || null;
            if (!currencySymbol && entry) {
                currencySymbol = entry.slice(w[0].length, w[1].length * -1).trim();
                if (format.indexOf(entry) === 0) {
                    currencyPosition = CurrencyPosition.Left;
                }
            }
        });
        const decimals: string[] = getEntries(format, /0/g);
        return {
            decimalLength: Math.max(decimals.length - 1, 0),
            decimalSeparator: DecimalSeparator.Comma,
            currencyPosition,
            currencySymbol
        };
    }

    private parseDatetimeFormat(format: string): IConfigDatetime {
        let type: DateType = DateType.DateOnly;
        format = format.toUpperCase();
        if (format.includes(DatePeriod.Hour.toUpperCase())) {
            type = DateType.DateTime;
            const time: string = format.slice(format.indexOf(DatePeriod.Hour.toUpperCase()));
            if (time.includes(DatePeriod.Second.toUpperCase())) {
                format = replaceAll(format, time, Global.DEFAULT_TIME_FORMAT_SEC);
            } else {
                format = replaceAll(format, time, Global.DEFAULT_TIME_FORMAT);
            }
        }
        return Object.assign({}, DefaultConfig[FieldType.datetime](), { format, type });
    }

}

const ExcelParser = new ExcelParserSingleton();

export default ExcelParser;